import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navLinks } from "@/mocks/content";
import { searchContent, typeLabels } from "@/utils/content";

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const transparent = isHome && !scrolled && !menuOpen && !searchOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [searchOpen]);

  const results = searchContent(keyword);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          transparent
            ? "bg-transparent"
            : "bg-background-50/95 backdrop-blur border-b border-background-200"
        }`}
      >
        <div className="max-w-site mx-auto px-4 md:px-6">
          <div className="h-16 md:h-20 flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0">
              <span
                className={`w-9 h-9 flex items-center justify-center rounded-md text-lg font-heading transition-colors ${
                  transparent ? "bg-background-50 text-primary-600" : "bg-primary-500 text-background-50"
                }`}
              >
                白
              </span>
              <span className="leading-tight">
                <span
                  className={`block font-heading text-lg font-semibold tracking-wide ${
                    transparent ? "text-background-50" : "text-foreground-950"
                  }`}
                >
                  白峰美羽
                </span>
                <span
                  className={`block text-[10px] tracking-[0.3em] ${
                    transparent ? "text-background-200" : "text-foreground-400"
                  }`}
                >
                  BAIFENG MEIYU
                </span>
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-8" aria-label="主导航">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    `text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      isActive
                        ? transparent
                          ? "text-accent-300 font-medium"
                          : "text-primary-600 font-medium"
                        : transparent
                          ? "text-background-100 hover:text-background-50"
                          : "text-foreground-600 hover:text-foreground-950"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-1.5 md:gap-3">
              <button
                type="button"
                onClick={() => setSearchOpen((v) => !v)}
                aria-label="搜索"
                className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                  transparent
                    ? "text-background-50 hover:bg-background-50/15"
                    : "text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={searchOpen ? "ri-close-line text-lg" : "ri-search-line text-lg"}></i>
              </button>
              <Link
                to="/contact"
                className="hidden md:inline-flex items-center px-4 py-2 rounded-md bg-accent-500 text-accent-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                联系我们
              </Link>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="菜单"
                className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                  transparent
                    ? "text-background-50 hover:bg-background-50/15"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
              </button>
            </div>
          </div>
        </div>

        {/* search panel */}
        {searchOpen && (
          <div className="border-t border-background-200 bg-background-50">
            <div className="max-w-site mx-auto px-4 md:px-6 py-5">
              <div className="flex items-center gap-3 bg-background-100 border border-background-300 rounded-md px-4 py-3">
                <i className="ri-search-line text-foreground-400 text-base"></i>
                <input
                  ref={inputRef}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索品牌故事、动态或产品…"
                  className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
              </div>
              {keyword.trim() && (
                <div className="mt-4">
                  {results.length > 0 ? (
                    <ul className="divide-y divide-background-200">
                      {results.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={`/detail/${item.id}`}
                            onClick={() => setSearchOpen(false)}
                            className="flex items-center gap-4 py-3 hover:bg-background-100 rounded-md px-2 transition-colors cursor-pointer"
                          >
                            <div className="w-12 h-12 rounded-md overflow-hidden bg-background-200 shrink-0">
                              <img
                                src={item.cover}
                                alt={item.title}
                                title={`${item.title} - 白峰美羽`}
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-foreground-900 truncate">
                                {item.title}
                              </p>
                              <p className="text-xs text-foreground-500 truncate">{item.summary}</p>
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">
                              {typeLabels[item.type]}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-foreground-500 py-4">
                      没有找到与「{keyword}」相关的内容，试试其他关键词。
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* mobile menu */}
        {menuOpen && (
          <div className="lg:hidden border-t border-background-200 bg-background-50">
            <nav className="max-w-site mx-auto px-4 py-3 flex flex-col" aria-label="移动端导航">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    `py-3 px-2 text-sm border-b border-background-100 last:border-0 cursor-pointer ${
                      isActive ? "text-primary-600 font-medium" : "text-foreground-700"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
                className="mt-3 mb-2 inline-flex justify-center items-center px-4 py-2.5 rounded-md bg-accent-500 text-accent-950 text-sm font-medium cursor-pointer"
              >
                联系我们
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}